/**
 * Contas a pagar: todo compromisso da empresa com data para sair do caixa. Cadastro manual (a conta
 * de luz, o fornecedor, o aluguel), com parcelamento opcional: "3x" gera três contas, uma por mês,
 * e a soma das parcelas é sempre o valor total. Pagar uma conta é um clique e dá baixa na data de
 * hoje; a data e o valor reais podem ser ajustados antes de confirmar.
 *
 * Fonte: `/api/platform/finance/payables` (migration 0056). Sem a rota, a tela diz que falta —
 * nunca mostra "nenhuma conta" como se o caixa estivesse limpo.
 */
import { createElement as h, useState, type ReactElement } from "react";
import { CalendarClock, CircleAlert, HandCoins, Plus } from "lucide-react";
import { StatusPanel, errorDetail } from "../components/StatusPanel.js";
import { DueBadge, InstallmentPreview } from "../components/FinanceBits.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import { formatBrl, formatCount } from "../format.js";
import { addDays, brlToCents, centsToInput, formatDate, todayIso } from "../finance/format.js";
import type { NewPayableInput, Payable, SettleInput } from "../finance/types.js";
import { useFinanceMutations, usePayables } from "../queries.js";
import { Badge, Button, Card, Drawer, EmptyState, Field, PageHeader, Segmented, StatTile, controlClass } from "../ui/index.js";

export type PayableFilter = "open" | "overdue" | "paid" | "all";
const PAGE_SIZE = 100;

export function payableSummary(payables: Payable[], today: string) {
  const open = payables.filter((p) => p.status === "open");
  const overdue = open.filter((p) => p.dueDate < today);
  const week = open.filter((p) => p.dueDate >= today && p.dueDate <= addDays(today, 7));
  const monthPrefix = today.slice(0, 7);
  const paidMonth = payables.filter((p) => p.status === "paid" && (p.paidAt ?? "").slice(0, 7) === monthPrefix);
  const sum = (rows: Payable[], pick: (p: Payable) => number) => rows.reduce((acc, p) => acc + pick(p), 0);
  return {
    openCents: sum(open, (p) => p.amountCents),
    openCount: open.length,
    overdueCents: sum(overdue, (p) => p.amountCents),
    overdueCount: overdue.length,
    weekCents: sum(week, (p) => p.amountCents),
    weekCount: week.length,
    paidMonthCents: sum(paidMonth, (p) => p.paidAmountCents ?? p.amountCents)
  };
}

export function filterPayables(payables: Payable[], filter: PayableFilter, query: string, today: string): Payable[] {
  const needle = query.trim().toLowerCase();
  return payables
    .filter((p) => {
      if (filter === "open") return p.status === "open";
      if (filter === "overdue") return p.status === "open" && p.dueDate < today;
      if (filter === "paid") return p.status === "paid";
      return true;
    })
    .filter((p) => !needle || [p.description, p.supplier ?? "", p.category ?? ""].some((value) => value.toLowerCase().includes(needle)))
    .sort((a, b) => (filter === "paid" ? (b.paidAt ?? "").localeCompare(a.paidAt ?? "") : a.dueDate.localeCompare(b.dueDate)));
}

interface PayableFormState {
  description: string;
  supplier: string;
  category: string;
  amount: string;
  firstDueDate: string;
  installments: string;
  notes: string;
}

function emptyForm(today: string): PayableFormState {
  return { description: "", supplier: "", category: "", amount: "", firstDueDate: today, installments: "1", notes: "" };
}

function NewPayableForm({ today, onSubmit, busy, error }: { today: string; onSubmit: (input: NewPayableInput) => void; busy: boolean; error: string | null }): ReactElement {
  const [form, setForm] = useState<PayableFormState>(emptyForm(today));
  const amountCents = brlToCents(form.amount);
  const installments = Math.max(1, Math.min(120, Math.trunc(Number(form.installments) || 1)));
  const valid = form.description.trim() !== "" && amountCents !== null && /^\d{4}-\d{2}-\d{2}$/.test(form.firstDueDate);
  const set = (key: keyof PayableFormState) => (event: { target: { value: string } }) => setForm({ ...form, [key]: event.target.value });
  return h(
    "form",
    {
      className: "flex flex-col gap-4",
      "data-form": "nova-conta-pagar",
      onSubmit: (event: { preventDefault: () => void }) => {
        event.preventDefault();
        if (!valid || amountCents === null) return;
        onSubmit({
          description: form.description.trim(),
          supplier: form.supplier.trim() || undefined,
          category: form.category.trim() || undefined,
          amountCents,
          firstDueDate: form.firstDueDate,
          installments,
          notes: form.notes.trim() || undefined
        });
      }
    },
    h(Field, { label: "O que é?", children: ({ id }) => h("input", { id, className: controlClass, value: form.description, onChange: set("description"), placeholder: "Ex.: Aluguel do galpão, Fornecedor AlgoLaser", required: true }) }),
    h("div", { className: "grid grid-cols-1 gap-3 sm:grid-cols-2" },
      h(Field, { label: "Fornecedor / credor", children: ({ id }) => h("input", { id, className: controlClass, value: form.supplier, onChange: set("supplier") }) }),
      h(Field, { label: "Categoria", children: ({ id }) => h("input", { id, className: controlClass, value: form.category, onChange: set("category"), placeholder: "Ex.: Aluguel, Impostos, Frete" }) })
    ),
    h("div", { className: "grid grid-cols-1 gap-3 sm:grid-cols-3" },
      h(Field, { label: "Valor total (R$)", children: ({ id }) => h("input", { id, className: controlClass, inputMode: "decimal", value: form.amount, onChange: set("amount"), placeholder: "0,00", required: true }) }),
      h(Field, { label: "Primeiro vencimento", children: ({ id }) => h("input", { id, className: controlClass, type: "date", value: form.firstDueDate, onChange: set("firstDueDate"), required: true }) }),
      h(Field, { label: "Parcelas", hint: "1 = à vista", children: ({ id }) => h("input", { id, className: controlClass, type: "number", min: 1, max: 120, value: form.installments, onChange: set("installments") }) })
    ),
    h(InstallmentPreview, { amountCents, installments, firstDueDate: form.firstDueDate }),
    h(Field, { label: "Observações", children: ({ id }) => h("textarea", { id, className: controlClass, rows: 2, value: form.notes, onChange: set("notes") }) }),
    error ? h("p", { className: "m-0 text-sm text-danger", role: "alert" }, error) : null,
    h(Button, { type: "submit", variant: "primary", disabled: !valid || busy }, busy ? "Salvando…" : "Cadastrar conta")
  );
}

function PayRow({ payable, today, busy, onSettle, onAction }: { payable: Payable; today: string; busy: boolean; onSettle: (id: string, input: SettleInput) => void; onAction: (id: string, action: "cancel" | "reopen") => void }): ReactElement {
  const [paying, setPaying] = useState(false);
  const [paidAt, setPaidAt] = useState(today);
  const [amount, setAmount] = useState(centsToInput(payable.amountCents));
  const amountCents = brlToCents(amount);
  return h(
    "tr",
    { className: "border-t border-border align-top", "data-payable": payable.id, "data-status": payable.status },
    h("td", { className: "px-2 py-3 tabular-nums" }, formatDate(payable.dueDate)),
    h("td", { className: "px-2 py-3" },
      h("span", { className: "font-semibold text-ink" }, payable.description),
      payable.installmentsTotal > 1 ? h("span", { className: "ml-2 text-xs text-muted" }, `parcela ${payable.installmentNo}/${payable.installmentsTotal}`) : null,
      payable.notes ? h("p", { className: "m-0 text-xs text-muted" }, payable.notes) : null
    ),
    h("td", { className: "px-2 py-3 text-sm text-muted" }, [payable.supplier, payable.category].filter(Boolean).join(" · ") || "—"),
    h("td", { className: "px-2 py-3 text-right font-extrabold tabular-nums" }, formatBrl(payable.status === "paid" ? (payable.paidAmountCents ?? payable.amountCents) : payable.amountCents)),
    h("td", { className: "px-2 py-3" }, h(DueBadge, { status: payable.status === "paid" ? "settled" : payable.status, dueDate: payable.dueDate, today, settledLabel: "Paga", settledAt: payable.paidAt })),
    h("td", { className: "px-2 py-3" },
      payable.status === "open" && !paying
        ? h("span", { className: "flex flex-wrap gap-2" },
            h(Button, { size: "sm", variant: "primary", onClick: () => setPaying(true), disabled: busy, "data-action": "pagar" }, "Pagar"),
            h(Button, { size: "sm", variant: "ghost", onClick: () => onAction(payable.id, "cancel"), disabled: busy }, "Cancelar")
          )
        : payable.status === "open"
          ? h("span", { className: "flex flex-wrap items-end gap-2" },
              h("input", { "aria-label": "Data do pagamento", type: "date", className: controlClass + " w-40", value: paidAt, onChange: (e: { target: { value: string } }) => setPaidAt(e.target.value) }),
              h("input", { "aria-label": "Valor pago (R$)", inputMode: "decimal", className: controlClass + " w-28", value: amount, onChange: (e: { target: { value: string } }) => setAmount(e.target.value) }),
              h(Button, { size: "sm", variant: "primary", disabled: busy || amountCents === null || !paidAt, onClick: () => { if (amountCents !== null) { onSettle(payable.id, { action: "pay", at: paidAt, amountCents }); setPaying(false); } }, "data-action": "confirmar-pagamento" }, "Confirmar"),
              h(Button, { size: "sm", variant: "ghost", onClick: () => setPaying(false) }, "Voltar")
            )
          : h(Button, { size: "sm", variant: "ghost", onClick: () => onAction(payable.id, "reopen"), disabled: busy }, "Reabrir")
    )
  );
}

export interface ContasPagarViewProps {
  /** `null` = rota não publicada neste ambiente. */
  payables: Payable[] | null;
  today?: string;
  onCreate?: (input: NewPayableInput) => void;
  onSettle?: (id: string, input: SettleInput) => void;
  onAction?: (id: string, action: "cancel" | "reopen") => void;
  busy?: boolean;
  error?: string | null;
  initialFilter?: PayableFilter;
}

export function ContasPagarView({ payables, today = todayIso(), onCreate, onSettle, onAction, busy = false, error = null, initialFilter = "open" }: ContasPagarViewProps): ReactElement {
  const [filter, setFilter] = useState<PayableFilter>(initialFilter);
  const [query, setQuery] = useState("");
  const [shown, setShown] = useState(PAGE_SIZE);
  const [creating, setCreating] = useState(false);
  const header = h(PageHeader, {
    title: "Contas a pagar",
    description: "Tudo que a empresa precisa pagar, com vencimento. Cadastre uma vez, parcele quando for o caso e dê baixa ao pagar.",
    actions: onCreate ? h(Button, { variant: "primary", onClick: () => setCreating(true), "data-action": "nova-conta" }, h(Plus, { className: "h-4 w-4", "aria-hidden": true }), "Nova conta") : undefined
  });

  if (payables === null) {
    return h("div", { className: "page page--contas-pagar flex flex-col gap-6", "data-page": "contas-pagar" }, header,
      h(StatusPanel, { kind: "awaiting_source", title: `Contas a pagar ${AWAITING_CONTRACT_LABEL}`, detail: "A rota /api/platform/finance/payables não respondeu neste ambiente." }));
  }

  const summary = payableSummary(payables, today);
  const visible = filterPayables(payables, filter, query, today);
  return h(
    "div",
    { className: "page page--contas-pagar flex flex-col gap-6", "data-page": "contas-pagar" },
    header,
    h("section", { "aria-label": "Resumo de contas a pagar", className: "grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" },
      h(StatTile, { label: "Em aberto", value: formatBrl(summary.openCents), hint: `${formatCount(summary.openCount)} conta(s)`, icon: HandCoins, "data-kpi": "pagar-aberto" }),
      h(StatTile, { label: "Vencidas", value: formatBrl(summary.overdueCents), hint: `${formatCount(summary.overdueCount)} conta(s) atrasada(s)`, icon: CircleAlert, "data-kpi": "pagar-vencidas" }),
      h(StatTile, { label: "Vencem em 7 dias", value: formatBrl(summary.weekCents), hint: `${formatCount(summary.weekCount)} conta(s)`, icon: CalendarClock, "data-kpi": "pagar-semana" }),
      h(StatTile, { label: "Pago no mês", value: formatBrl(summary.paidMonthCents), icon: HandCoins, "data-kpi": "pagar-pago-mes" })
    ),
    error ? h(StatusPanel, { kind: "error", title: "Não foi possível concluir a operação", detail: error }) : null,
    h(Card, null,
      h("div", { className: "mb-4 flex flex-wrap items-end justify-between gap-3" },
        h(Segmented<PayableFilter>, {
          label: "Filtrar contas",
          value: filter,
          options: [{ value: "open", label: "Em aberto" }, { value: "overdue", label: "Vencidas" }, { value: "paid", label: "Pagas" }, { value: "all", label: "Todas" }],
          onChange: (value) => { setFilter(value); setShown(PAGE_SIZE); }
        }),
        h("input", { type: "search", "aria-label": "Buscar conta", placeholder: "Buscar por descrição, fornecedor ou categoria", className: controlClass + " w-full sm:w-80", value: query, onChange: (event: { target: { value: string } }) => { setQuery(event.target.value); setShown(PAGE_SIZE); } })
      ),
      visible.length === 0
        ? h(EmptyState, { title: payables.length === 0 ? "Nenhuma conta cadastrada" : "Nenhuma conta neste filtro", description: payables.length === 0 ? "Use “Nova conta” para cadastrar o que você precisa pagar." : "Ajuste o filtro ou a busca." })
        : h("div", { className: "overflow-x-auto" },
            h("table", { className: "w-full text-left text-sm", "data-table": "contas-pagar" },
              h("thead", null, h("tr", { className: "text-xs uppercase text-muted" },
                ["Vencimento", "Descrição", "Fornecedor / categoria", "Valor", "Situação", "Ações"].map((title, index) => h("th", { key: title, scope: "col", className: "px-2 py-2" + (index === 3 ? " text-right" : "") }, title)))),
              h("tbody", null, visible.slice(0, shown).map((payable) => h(PayRow, { key: payable.id, payable, today, busy, onSettle: (id, input) => onSettle?.(id, input), onAction: (id, action) => onAction?.(id, action) })))
            ),
            visible.length > shown ? h("div", { className: "mt-3 flex items-center gap-3 text-sm text-muted" }, h("span", null, `Mostrando ${shown} de ${formatCount(visible.length)}`), h(Button, { size: "sm", onClick: () => setShown(shown + PAGE_SIZE) }, "Mostrar mais")) : null
          ),
      h("p", { className: "m-0 mt-3 text-xs text-muted" }, `${formatCount(payables.length)} conta(s) no total · ${payables.filter((p) => p.status === "paid").length > 0 ? "inclui pagas" : "nenhuma paga ainda"}.`)
    ),
    onCreate
      ? h(Drawer, { open: creating, title: "Nova conta a pagar", onClose: () => setCreating(false), children: h(NewPayableForm, { today, busy, error, onSubmit: (input) => { onCreate(input); setCreating(false); } }) })
      : null,
    h(Badge, { tone: "neutral", className: "w-fit" }, "Gerencial — não substitui a contabilidade")
  );
}

export function ContasPagar(): ReactElement {
  const query = usePayables();
  const { createPayable, updatePayable } = useFinanceMutations();
  if (query.isPending) return h(StatusPanel, { kind: "loading", title: "Carregando contas a pagar…" });
  if (query.error) return h(StatusPanel, { kind: "error", title: "Não foi possível carregar as contas a pagar", detail: errorDetail(query.error) });
  const mutationError = createPayable.error ?? updatePayable.error;
  return h(ContasPagarView, {
    payables: query.data ?? null,
    onCreate: (input) => createPayable.mutate(input),
    onSettle: (id, input) => updatePayable.mutate({ id, input }),
    onAction: (id, action) => updatePayable.mutate({ id, input: { action } }),
    busy: createPayable.isPending || updatePayable.isPending,
    error: mutationError ? mutationError.message : null
  });
}
